import { Alert, Button } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { z } from 'zod';
import { apiUrl } from '../config/api';
import { apiClient } from '../lib/apiClient';
import { apiResponseSchema } from '../lib/schemas';

const changeSchema = z.object({
  id: z.number(),
  date_of_year: z.string(),
  calendar_week: z.string(),
  change_type: z.string(),
  old_kind: z.string().nullable(),
  old_hours: z.number().nullable(),
  new_kind: z.string().nullable(),
  new_hours: z.number().nullable(),
});
type ZmChange = z.infer<typeof changeSchema>;

const kind = (k: string | null) => (k === 'absence' ? 'Abwesenheit' : 'Urlaub');
const hours = (h: number | null) => `${(h ?? 0).toLocaleString('de-DE', { maximumFractionDigits: 2 })} h`;

const describe = (c: ZmChange) => {
  switch (c.change_type) {
    case 'added':
      return `${kind(c.new_kind)} hinzugefügt (${hours(c.new_hours)})`;
    case 'removed':
      return `${kind(c.old_kind)} entfernt`;
    default:
      return `${kind(c.old_kind)} (${hours(c.old_hours)}) geändert zu ${kind(c.new_kind)} (${hours(c.new_hours)})`;
  }
};

// Information only: ZM changes the sync applied in the employee's closed weeks.
export function ZmChangeBanner({ employeeId }: { employeeId?: string | null }) {
  const queryClient = useQueryClient();
  const { data: changes = [] } = useQuery({
    queryKey: ['zmChanges', employeeId],
    queryFn: async (): Promise<ZmChange[]> =>
      (
        await apiClient.get(apiUrl.zmChanges(employeeId!), {
          validate: apiResponseSchema(z.array(changeSchema)),
          context: 'zmChanges',
        })
      ).data,
    enabled: !!employeeId,
    retry: false,
  });

  if (!employeeId || changes.length === 0) return null;

  const acknowledge = async () => {
    try {
      await apiClient.post(apiUrl.zmChangesAck(employeeId), undefined, { context: 'zmChangesAck' });
    } finally {
      queryClient.invalidateQueries({ queryKey: ['zmChanges', employeeId] });
    }
  };

  return (
    <Alert color="blue" mb="sm" title="ZM-Änderung in abgeschlossener Woche">
      {changes.map((c) => (
        <div key={c.id}>
          {c.calendar_week.replace(/^\d{4}\s+CW/, 'KW')} – {format(parseISO(c.date_of_year), 'EEE dd.MM.yyyy')}:{' '}
          {describe(c)}
        </div>
      ))}
      <Button size="xs" mt="xs" variant="light" onClick={acknowledge}>
        Zur Kenntnis genommen
      </Button>
    </Alert>
  );
}
